import apiClient from './apiClient';

// Re-export types from organization-types for backward compatibility
export type {
	OrganizationUser,
	Organization,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	InviteCollaboratorRequest,
	UpdateOrganizationUserRequest,
	PendingInvitation,
} from '../../type/organization-types';

import type {
	OrganizationUser,
	Organization,
	CreateOrganizationRequest,
	UpdateOrganizationRequest,
	InviteCollaboratorRequest,
	UpdateOrganizationUserRequest,
	PendingInvitation,
} from '../../type/organization-types';

const getOrganizations = async (): Promise<Organization[]> => {
	try {
		const response = await apiClient.get('/api/organizations');
		return response.data;
	} catch (error) {
		console.error('Error fetching organizations:', error);
		throw error;
	}
};

const getOrganizationById = async (id: string): Promise<Organization> => {
	try {
		const response = await apiClient.get(`/api/organizations/${id}`);
		return response.data;
	} catch (error) {
		console.error(`Error fetching organization with ID ${id}:`, error);
		throw error;
	}
};

const createOrganization = async (data: CreateOrganizationRequest): Promise<Organization> => {
	try {
		const response = await apiClient.post('/api/organizations', data);
		return response.data;
	} catch (error) {
		console.error('Error creating organization:', error);
		throw error;
	}
};

const updateOrganization = async (
	id: string,
	data: UpdateOrganizationRequest,
): Promise<Organization> => {
	try {
		const response = await apiClient.put(`/api/organizations/${id}`, data);
		return response.data;
	} catch (error) {
		console.error(`Error updating organization with ID ${id}:`, error);
		throw error;
	}
};

const inviteCollaborator = async (
	orgId: string,
	data: InviteCollaboratorRequest,
): Promise<void> => {
	await apiClient.post(`/api/organizations/${orgId}/collaborators/invite`, data);
};

const getPendingInvitations = async (orgId: string): Promise<PendingInvitation[]> => {
	const response = await apiClient.get(`/api/organizations/${orgId}/invitations/pending`);
	return response.data;
};

const removeCollaborator = async (orgId: string, userId: string): Promise<void> => {
	await apiClient.delete(`/api/organizations/${orgId}/collaborators/${userId}`);
};

/**
 * Update an organization user's role and permissions
 */
const updateOrganizationUser = async (
	orgId: string,
	userId: string,
	data: UpdateOrganizationUserRequest,
): Promise<void> => {
	await apiClient.patch(`/api/organizations/${orgId}/collaborators/${userId}`, data);
};

/**
 * Resend invitation to a pending user
 */
const resendInvitation = async (orgId: string, userId: string): Promise<void> => {
	await apiClient.post(`/api/organizations/${orgId}/users/${userId}/resend-invite`);
};

/**
 * Get all users (team members) for an organization
 */
const getOrganizationUsers = async (orgId: string): Promise<OrganizationUser[]> => {
	const response = await apiClient.get(`/api/organizations/${orgId}/users`);
	return response.data;
};

/**
 * Upload organization logo
 * @param orgId - Organization ID
 * @param file - Image file (max 5MB, image/*)
 * @returns Object containing the new logo URL
 */
const uploadLogo = async (orgId: string, file: File): Promise<{ logoUrl: string }> => {
	const formData = new FormData();
	formData.append('file', file);
	const response = await apiClient.post(`/api/organizations/${orgId}/logo`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
	return response.data;
};

export const organizationService = {
	getOrganizations,
	getOrganizationById,
	createOrganization,
	updateOrganization,
	inviteCollaborator,
	removeCollaborator,
	getPendingInvitations,
	updateOrganizationUser,
	resendInvitation,
	getOrganizationUsers,
	uploadLogo,
};

export default organizationService;
